import { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Skeleton } from 'antd';

import { SurgeryClassificationResponse, useSurgeryClassification } from 'all-in-one-shared-api';
import SurgeryClassificationIcon from 'assets/images/benefits/surgery-classification/surgery-classification.svg';
import FilterSection from './components/filterSection/FilterSection';
import SurgeryList from './components/surgeryInfo/SurgeryList';
import NoResult from './components/noResult/NoResult';
import { SurgeryItem } from './types/SurgeryType';
import { PageTitle } from 'components';
import { SelectOption } from 'components/select/Select';
import { getTextByLocale } from './utils';
import { useKeycloakReady } from 'hooks/useKeycloakReady';

const DEFAULT_FILTER = 'All';

function SurgeryClassificationPage() {
  const { t, i18n } = useTranslation();
  const { token, ready } = useKeycloakReady();
  const [searchTerm, setSearchTerm] = useState<string>('');

  const [specialtySelection, setSpecialtySelection] = useState(DEFAULT_FILTER);
  const [classificationSelection, setClassificationSelection] = useState(DEFAULT_FILTER);

  const [dirtyFilters, setDirtyFilters] = useState({ specialty: false, classification: false });

  const specialtySelectionFilter = specialtySelection == DEFAULT_FILTER ? undefined : specialtySelection;
  const classificationSelectionFilter = classificationSelection == DEFAULT_FILTER ? undefined : classificationSelection;

  const { shouldFetch, data, isValidating } = useSurgeryClassification(
    { name: 'SurgeryClassification', searchTerm, specialtySelectionFilter, classificationSelectionFilter },
    token || '',
    searchTerm,
    specialtySelectionFilter,
    classificationSelectionFilter,
  );

  useEffect(() => {
    shouldFetch(ready);
  }, [ready, shouldFetch]);

  const getSurgerySpecialties = (surgeryData: Array<SurgeryClassificationResponse> | undefined) => {
    const items: Array<SelectOption> = [];

    if (!surgeryData) {
      return items;
    }

    items.push({
      id: DEFAULT_FILTER,
      label: t('benefits.surgery.allDoctorSpecialty'),
    });

    surgeryData.forEach((specialty) =>
      items.push({
        id: specialty.name.description || '',
        label: getTextByLocale(i18n.language, specialty.name),
      }),
    );

    return items;
  };

  const surgeryData = useMemo(() => {
    if (!data) return [];

    return data
      .map((specialty) => {
        return specialty.categories.map((category) => ({
          id: `${specialty.name.description}-${category.categoryId}`,
          title: getTextByLocale(i18n.language, specialty.name),
          subtitle: getTextByLocale(i18n.language, category),
          ...(
            [
              ['minorSurgeries', 'MINOR'],
              ['intermediateSurgeries', 'INTERMEDIATE'],
              ['majorSurgeries', 'MAJOR'],
              ['complexSurgeries', 'COMPLEX'],
            ] as const
          ).reduce(
            (acc, [key, severity]) => {
              acc[key] = getSurgeryProcedures(specialty.surgeries, severity, category.categoryId, i18n.language);

              return acc;
            },
            {} as {
              minorSurgeries: ReturnType<typeof getSurgeryProcedures>;
              intermediateSurgeries: ReturnType<typeof getSurgeryProcedures>;
              majorSurgeries: ReturnType<typeof getSurgeryProcedures>;
              complexSurgeries: ReturnType<typeof getSurgeryProcedures>;
            },
          ),
        }));
      })
      .flat();
  }, [data, i18n.language]);

  return (
    <>
      <PageTitle icon={SurgeryClassificationIcon} title={t('benefits.checkSurgeryClassification')} />
      <FilterSection
        handleSubmitSearch={setSearchTerm}
        surgerySpecialties={getSurgerySpecialties(data)}
        /**
         * Note(Anson Kan): to keep filter gray when is not dirty.
         * Also, without controlling the values, they are stuck at the previous language after language changes
         */
        specialty={dirtyFilters.specialty ? specialtySelection : undefined}
        classification={dirtyFilters.classification ? classificationSelection : undefined}
        handleSpecialtyChange={(v) => {
          setSpecialtySelection(v);
          setDirtyFilters((prev) => ({ ...prev, specialty: true }));
        }}
        handleClassificationChange={(v) => {
          setClassificationSelection(v);
          setDirtyFilters((prev) => ({ ...prev, classification: true }));
        }}
      />
      {isValidating && <Skeleton active />}
      {data && data.length == 0 ? <NoResult /> : <SurgeryList data={surgeryData} />}
    </>
  );
}

export default SurgeryClassificationPage;

function getSurgeryProcedures(
  surgeries: SurgeryClassificationResponse['surgeries'],
  type: string,
  categoryId: number,
  language: string,
) {
  return surgeries
    .filter((s) => s.severity === type)
    .map((s) => s.procedures || [])
    .flat()
    .filter((p) => p.categoryId === categoryId)
    .map((d) => {
      const result: SurgeryItem = {
        id: `${d.categoryId}-${d.description}`,
        title: getTextByLocale(language, d),
      };

      if (Object.keys(d.remarks ?? {}).length > 0) {
        result['info'] = {
          title: getTextByLocale(language, d),
          body: getTextByLocale(language, d.remarks),
        };
      }

      return result;
    });
}
